// An appended Word original shown as its text, paragraph by paragraph and
// table by table, each typed into in place, drawn with the original's own
// fonts, sizes, alignment, indents, spacing and list markers (AGP
// feedback: the Japanese part must look like the file). Edits are stored
// by paragraph number and applied to the file itself at generate time.

import type { CSSProperties, ReactNode } from "react";
import { Editable } from "@/components/preview/Editable";
import type { SourceParaStyle, SourceTextBlock, SourceTextCell, SourceTextDoc, SourceTextEdits, SourceTextPara } from "@agp/shared";
import type { PageGeometry } from "@/components/preview/AppendixPages";

export interface TextSource {
  id: string;
  filename: string;
  /** Null while loading. */
  doc: SourceTextDoc | null;
  textEdits: SourceTextEdits;
  onChange?: (edits: SourceTextEdits) => void;
}

const pt = (points: number) => (points * 96) / 72;

/** A CSS font stack led by the document's own font, with Japanese
 *  fallbacks of the same flavour (明朝/serif or ゴシック/sans). */
export function fontStackFor(name: string | undefined): string {
  const n = name ?? "";
  const mincho = /明朝|Mincho|Serif|Times|Merriweather|Georgia|Garamond/i.test(n);
  const fallback = mincho
    ? "'Hiragino Mincho ProN', 'Yu Mincho', 'MS Mincho', 'Noto Serif CJK JP', serif"
    : "'Hiragino Sans', 'Yu Gothic', 'Meiryo', 'MS Gothic', 'Noto Sans CJK JP', sans-serif";
  return n ? `'${n.replace(/'/g, "")}', ${fallback}` : fallback;
}

function paraCss(style: SourceParaStyle | undefined, defaults: SourceParaStyle): CSSProperties {
  const s = { ...defaults, ...style };
  const css: CSSProperties = {
    fontFamily: fontStackFor(s.font),
    fontSize: pt(s.fontPt ?? 10.5),
    fontWeight: s.bold ? 700 : 400,
    fontStyle: s.italic ? "italic" : "normal",
    textDecoration: s.underline ? "underline" : undefined,
    color: s.color ? `#${s.color}` : undefined,
    textAlign: s.align === "justify" ? "justify" : (s.align ?? "left"),
    paddingLeft: pt(s.indentLeft ?? 0),
    textIndent: pt(s.indentFirst ?? 0),
    marginTop: pt(s.spaceBefore ?? 0),
    marginBottom: pt(s.spaceAfter ?? 0),
    lineHeight: s.lineHeight ? `${s.lineHeight * 1.3}` : 1.3,
  };
  return css;
}

export function SourceTextView({
  source,
  geometry,
  chrome,
  label,
}: {
  source: TextSource;
  geometry: PageGeometry;
  chrome: React.ReactNode;
  label: string;
}) {
  const editing = source.onChange != null;
  const edits = source.textEdits;
  const removed = new Set(edits.removed);
  const defaults = source.doc?.defaults ?? { font: undefined, fontPt: 10.5 };
  const textOf = (id: number, original: string) => edits.texts[String(id)] ?? original;

  const setText = (id: number, original: string, text: string) => {
    const texts = { ...edits.texts };
    if (text === original) delete texts[String(id)];
    else texts[String(id)] = text;
    source.onChange?.({ ...edits, texts });
  };
  const remove = (id: number) => source.onChange?.({ ...edits, removed: [...edits.removed, id].sort((a, b) => a - b) });
  const restore = (id: number) => source.onChange?.({ ...edits, removed: edits.removed.filter((p) => p !== id) });

  // Table rows: removed ones stay visible, struck through, until restored;
  // added ones are typed into cell by cell and formatted like the row
  // they follow. Tables and rows are numbered as in the original file.
  const removedRows = edits.removedRows ?? [];
  const addedRows = edits.addedRows ?? [];
  const tableIndexOf = (blockIndex: number) => source.doc!.blocks.slice(0, blockIndex).filter((x) => x.kind === "table").length;
  const isRowRemoved = (t: number, r: number) => removedRows.some((x) => x.table === t && x.row === r);
  const removeRow = (t: number, r: number) => source.onChange?.({ ...edits, removedRows: [...removedRows, { table: t, row: r }] });
  const restoreRow = (t: number, r: number) => source.onChange?.({ ...edits, removedRows: removedRows.filter((x) => !(x.table === t && x.row === r)) });
  const addRow = (t: number, after: number, cellCount: number) =>
    source.onChange?.({ ...edits, addedRows: [...addedRows, { table: t, after, cells: Array<string>(cellCount).fill("") }] });
  const setAddedCell = (index: number, cell: number, text: string) =>
    source.onChange?.({ ...edits, addedRows: addedRows.map((r, k) => (k === index ? { ...r, cells: r.cells.map((c, j) => (j === cell ? text : c)) } : r)) });
  const dropAddedRow = (index: number) => source.onChange?.({ ...edits, addedRows: addedRows.filter((_, k) => k !== index) });

  const rowTools = (children: ReactNode) => (
    <span contentEditable={false} style={{ position: "absolute", right: "100%", top: 0, bottom: 0, display: "flex", alignItems: "center", gap: 2, paddingRight: 4, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
  const toolButton = (title: string, onClick: () => void, label: string) => (
    <button type="button" title={title} onClick={onClick} style={{ ...miniButton, marginLeft: 0, padding: "0 5px", lineHeight: "14px" }}>
      {label}
    </button>
  );

  const table = (b: Extract<SourceTextBlock, { kind: "table" }>, key: string, t: number) => {
    const side = (on: boolean) => (on ? "1px solid #444" : "none");
    const cellStyle = (cell: SourceTextCell, faded = false): CSSProperties => ({
      ...(cell.borders
        ? { borderTop: side(cell.borders.top), borderRight: side(cell.borders.right), borderBottom: side(cell.borders.bottom), borderLeft: side(cell.borders.left) }
        : { border: b.bordered ? "1px solid #444" : undefined }),
      padding: "1px 5px",
      verticalAlign: cell.vAlign === "center" ? "middle" : (cell.vAlign ?? "top"),
      position: "relative",
      ...(faded ? { opacity: 0.45, textDecoration: "line-through" } : {}),
    });
    const columns = b.widths.length || Math.max(1, ...b.rows.map((r) => r.reduce((n, c) => n + c.span, 0)));
    const addedAfter = (after: number) =>
      addedRows.map((r, index) => ({ r, index })).filter(({ r }) => r.table === t && r.after === after);
    const templateRow = (after: number) => b.rows[Math.max(0, Math.min(b.rows.length - 1, after))] ?? [];
    const addedTr = ({ r, index }: { r: (typeof addedRows)[number]; index: number }) => {
      const template = templateRow(r.after);
      const used = template.reduce((n, c) => n + c.span, 0);
      return (
        <tr key={`a${index}`} data-added-row={index}>
          {template.map((cell, j) => (
            <td key={j} colSpan={cell.span > 1 ? cell.span : undefined} style={cellStyle(cell)}>
              <div style={paraCss(cell.paras[0]?.style, defaults)}>
                <Editable
                  id={`source.${source.id}.row.${index}.c.${j}`}
                  value={r.cells[j] ?? ""}
                  readOnly={!editing}
                  multiline
                  style={{ minHeight: "1.3em" }}
                  onCommit={(text) => setAddedCell(index, j, text)}
                />
              </div>
              {editing && j === 0 && rowTools(toolButton("Remove this added row", () => dropAddedRow(index), "×"))}
            </td>
          ))}
          {used < columns && <td colSpan={columns - used} style={{ border: b.bordered ? "1px solid #444" : undefined }} />}
        </tr>
      );
    };
    return (
      <table key={key} data-source-table={t} style={{ borderCollapse: "collapse", width: "100%", margin: "2px 0 6px", tableLayout: b.widths.length > 0 ? "fixed" : "auto" }}>
        {b.widths.length > 0 && (
          <colgroup>
            {b.widths.map((w, ci) => (
              <col key={ci} style={{ width: `${w}%` }} />
            ))}
          </colgroup>
        )}
        <tbody>
          {addedAfter(-1).map(addedTr)}
          {b.rows.flatMap((row, ri) => {
            const gone = isRowRemoved(t, ri);
            return [
              <tr key={ri} data-source-row={ri} style={{ height: b.rowHeights?.[ri] ? pt(b.rowHeights[ri]!) : undefined }}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    colSpan={cell.span > 1 ? cell.span : undefined}
                    rowSpan={cell.rowSpan != null && cell.rowSpan > 1 ? cell.rowSpan : undefined}
                    style={cellStyle(cell, gone)}
                  >
                    {cell.paras.map((p) => paragraph(p, true))}
                    {editing &&
                      ci === 0 &&
                      rowTools(
                        gone ? (
                          toolButton("Put this row back", () => restoreRow(t, ri), "Restore")
                        ) : (
                          <>
                            {toolButton("Add a row below", () => addRow(t, ri, row.length), "+")}
                            {toolButton("Remove this row", () => removeRow(t, ri), "×")}
                          </>
                        ),
                      )}
                  </td>
                ))}
              </tr>,
              ...addedAfter(ri).map(addedTr),
            ];
          })}
        </tbody>
      </table>
    );
  };

  const paragraph = (p: SourceTextPara, inCell = false) => {
    if (removed.has(p.id)) {
      return (
        <div
          key={p.id}
          data-removed-para={p.id}
          style={{ color: "#9aa0a6", fontSize: 11, fontFamily: "system-ui, sans-serif", padding: "1px 0" }}
        >
          <span style={{ textDecoration: "line-through" }}>{textOf(p.id, p.text) || "(empty line)"}</span>
          {editing && (
            <button type="button" data-restore-para={p.id} onClick={() => restore(p.id)} style={miniButton}>
              Restore
            </button>
          )}
        </div>
      );
    }
    const css = paraCss(p.style, defaults);
    // A list marker sits in the hanging indent, as Word draws it.
    const bulletWidth = p.bullet ? Math.max(pt(12), -pt(p.style?.indentFirst ?? 0)) : 0;
    return (
      <div key={p.id} data-source-para={p.id} style={{ ...css, position: "relative", ...(p.bullet ? { paddingLeft: Number(css.paddingLeft) + (p.style?.indentFirst && p.style.indentFirst < 0 ? 0 : bulletWidth), textIndent: 0 } : {}) }}>
        {p.bullet && (
          <span style={{ position: "absolute", left: Number(css.paddingLeft) - (p.style?.indentFirst && p.style.indentFirst < 0 ? -pt(p.style.indentFirst) : 0), width: bulletWidth }} data-bullet="">
            {p.bullet}
          </span>
        )}
        <Editable
          id={`source.${source.id}.p.${p.id}`}
          value={textOf(p.id, p.text)}
          readOnly={!editing}
          multiline
          placeholder={editing && !inCell ? "(empty line — Backspace removes it)" : undefined}
          style={{ minHeight: "1.3em" }}
          onCommit={(t) => setText(p.id, p.text, t)}
          onEmptyBackspace={() => remove(p.id)}
        />
      </div>
    );
  };

  return (
    <div
      data-source-text={source.id}
      style={{
        width: geometry.pageW,
        minHeight: geometry.pageH,
        background: "#ffffff",
        position: "relative",
        boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
        ...paraCss(undefined, defaults),
        paddingLeft: 0,
        margin: 0,
      }}
    >
      {chrome}
      <div
        style={{
          position: "absolute",
          top: 8,
          left: geometry.marginLeft,
          fontFamily: "system-ui, sans-serif",
          fontSize: 10,
          color: "#9aa0a6",
        }}
      >
        {source.filename} · text view{editing ? " · click a line to edit it, Backspace on an empty line removes it" : ""}
      </div>
      <div
        style={{
          padding: `${geometry.marginTop}px ${geometry.pageW - geometry.marginLeft - geometry.contentW}px ${geometry.marginTop}px ${geometry.marginLeft}px`,
          whiteSpace: "pre-wrap",
        }}
      >
        {source.doc == null && (
          <div style={{ color: "#9aa0a6", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>Reading {source.filename}…</div>
        )}
        {source.doc?.blocks.map((b, i) => (b.kind === "para" ? paragraph(b) : table(b, `t${i}`, tableIndexOf(i))))}
      </div>
      <span style={{ position: "absolute", right: 10, bottom: 6, fontFamily: "system-ui, sans-serif", fontSize: 9, color: "#B0B3B8" }}>
        {label}
      </span>
    </div>
  );
}

const miniButton: CSSProperties = {
  marginLeft: 8,
  fontFamily: "system-ui, sans-serif",
  fontSize: 10,
  padding: "1px 6px",
  border: "1px solid #c4c8cf",
  borderRadius: 3,
  background: "#fff",
  cursor: "pointer",
};
